/**
 * Bayi paneli (Hayat/Abant) durumunun kullanıcıya gösterimi.
 *
 * `Order.externalStatusRaw` ham durum metni (`"Yolda"`, `"Araç Listesinde"`, `"Teslim Edildi(Bayi)"` …).
 * Bu sınıflandırıcı panelin GERÇEK aşamasını rozet olarak gösterir — bizdeki sipariş durumu
 * (`Order.status`) çoğu zaman bu sınıflandırmadan geridedir; ham metin gösterilmezse kullanıcı
 * panelin söylediğini görmez.
 *
 * Üç parça:
 *   - `panelStage(source, raw)` → `{ brand, text, tone }` veya `null`. `brand` marka adı,
 *     `text` panelin ham etiketi (değiştirilmez), `tone` renk için kısa kategori.
 *   - `orderStatusText(status, raw)` → `Order.status` etiketi. `HANDLED_EXTERNALLY` için
 *     panelOutcome'a göre alt anahtardan okur; diğer durumlar `orderStatus.<status>`'tan.
 *
 * Defter DEĞİŞMEZ; bu yalnız sunum.
 */
import { t, type MessageKey } from './i18n';
import { panelOutcome, type HayatOutcome } from './hayat-outcome';

export type PanelStageTone = 'new' | 'progress' | 'done' | 'cancelled';

export interface PanelStage {
  brand: string;
  text: string;
  tone: PanelStageTone;
}

/** Panelin kaynakları için ortak marka adı (rozet ön eki). */
const PANEL_BRAND_KEYS: Record<string, MessageKey> = {
  HAYAT_PANEL: 'panelStage.brand.HAYAT_PANEL',
  ABANT_PANEL: 'panelStage.brand.ABANT_PANEL',
};

/**
 * Kaynak HAYAT_PANEL/ABANT_PANEL ve ham durum doluysa `{ brand, text, tone }` döner.
 * Aksi hâlde `null` — panel kanalları dışındaki siparişlerde (telefon/WhatsApp/Getir vb.) hiçbir
 * şey çizilmez.
 *
 * `text` ham metin OLDUĞU GİBİ döner (trim). Panelin kendi kelimesi değiştirilmez —
 * çeviri/anonimleştirme riski yok, ekranda panel ne diyorsa o görünür.
 *
 * `tone`:
 *   - `done`       — `panelOutcome === 'delivered'`
 *   - `cancelled`  — `panelOutcome === 'cancelled'`
 *   - `new`        — `panelOutcome === 'pending'` ve ham metin "Yeni" ile başlıyor (panel henüz işleme almamış)
 *   - `progress`   — `panelOutcome === 'pending'` ve diğer (panelde işlemde)
 */
export function panelStage(source: string, raw: string | null | undefined): PanelStage | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const brandKey = PANEL_BRAND_KEYS[source];
  if (!brandKey) return null;
  const outcome = panelOutcome(trimmed);
  if (outcome === null) return null;
  return {
    brand: t(brandKey),
    text: trimmed,
    tone: toneFor(outcome, trimmed),
  };
}

function toneFor(outcome: HayatOutcome, raw: string): PanelStageTone {
  if (outcome === 'delivered') return 'done';
  if (outcome === 'cancelled') return 'cancelled';
  // pending: panelde hâlâ işlemde. "Yeni" panelin siparişi henüz işleme almadığını söyler;
  // diğer her şey ("Araç Listesinde", "Yolda" …) panelde işlemde demek.
  return rawStartsWithNew(raw) ? 'new' : 'progress';
}

/**
 * Panel ham durum metninden ton (`'new'` | `'progress'` | `'done'` | `'cancelled'`).
 * `null` dönerse metin boş ya da sınıflandırılamaz — API bu durumu çağıran yerde "pending"
 * (yeni/bekliyor) sayabilir. Sınıflandırma `panelStage`'le birebir aynıdır; bu yardımcı
 * sunum yerine sayım/etiketleme için çağrılır (`panelStage` i18n çağırır, bunu çağırmaz).
 */
export function panelRawTone(raw: string | null | undefined): PanelStageTone | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const outcome = panelOutcome(trimmed);
  if (outcome === null) return null;
  return toneFor(outcome, trimmed);
}

/** `tr-TR` büyük harf + NFC normalize — panelOutcome ile aynı dönüşüm, "Yeni" bayrağı için. */
function rawStartsWithNew(raw: string): boolean {
  const norm = raw.normalize('NFC').toLocaleUpperCase('tr-TR').trim();
  return norm.startsWith('YENİ') || norm.startsWith('YENI');
}

const HANDLED_EXTERNALLY_SUB: Record<HayatOutcome, MessageKey> = {
  delivered: 'orderStatus.HANDLED_EXTERNALLY.delivered',
  cancelled: 'orderStatus.HANDLED_EXTERNALLY.cancelled',
  pending: 'orderStatus.HANDLED_EXTERNALLY.pending',
};

/**
 * `Order.status` için kullanıcı metni. `HANDLED_EXTERNALLY` panelin sonucuna göre ayrılır:
 * panel "teslim edildi" dediyse "Panelde teslim edildi", "iptal" dediyse "Panelde iptal",
 * hâlâ panelde işlemde ise "Panelde işlemde". Diğer tüm durumlar `orderStatus.<status>`'tan.
 *
 * Sıralama: `raw` boş/null ise genel `orderStatus.HANDLED_EXTERNALLY` ("Panelde") döner —
 * ham durum okunmamışsa "kapandı" yazmak yanıltıcıdır, kullanıcı panelde ne olduğunu zaten
 * ayrı bir alandan görür.
 */
export function orderStatusText(status: string, raw: string | null | undefined): string {
  if (status === 'HANDLED_EXTERNALLY') {
    const outcome = raw ? panelOutcome(raw) : null;
    if (outcome) return t(HANDLED_EXTERNALLY_SUB[outcome]);
    return t('orderStatus.HANDLED_EXTERNALLY');
  }
  return t(`orderStatus.${status}` as MessageKey);
}
